import { useCallback, useMemo, useRef, useState } from 'react';
import {
  buildLUT,
  type CurveChannel,
  type CurvePoint,
  type CurvesValue,
} from '../../lib/adjustments/curves';
import type { AdjustmentPanelProps } from './registry';

const CHANNELS: { value: CurveChannel; label: string; color: string }[] = [
  { value: 'rgb', label: 'RGB', color: '#888' },
  { value: 'r', label: 'R', color: '#ef4444' },
  { value: 'g', label: 'G', color: '#22c55e' },
  { value: 'b', label: 'B', color: '#3b82f6' },
];

const GRAPH_SIZE = 256;
const POINT_RADIUS = 5;
const DELETE_DRAG_DISTANCE = 30; // px outside graph bounds → delete

export function CurvesPanel({ value, onChange, histogram }: AdjustmentPanelProps<CurvesValue>) {
  const [activeChannel, setActiveChannel] = useState<CurveChannel>('rgb');

  const activePoints = value[activeChannel];
  const setActivePoints = useCallback(
    (next: CurvePoint[]) => {
      onChange({ ...value, [activeChannel]: next });
    },
    [activeChannel, onChange, value],
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-1">
        {CHANNELS.map((ch) => (
          <button
            key={ch.value}
            type="button"
            className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
              activeChannel === ch.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-accent'
            }`}
            onClick={() => setActiveChannel(ch.value)}
          >
            {ch.label}
          </button>
        ))}
      </div>

      <CurveEditor
        points={activePoints}
        onChange={setActivePoints}
        color={CHANNELS.find((c) => c.value === activeChannel)?.color ?? '#888'}
        histogram={activeChannel === 'rgb' ? histogram : undefined}
      />

      <p className="text-[11px] text-muted-foreground">
        Click the curve to add a point; drag to move; drag off the graph to delete.
      </p>
    </div>
  );
}

/** Interactive curve editor. Renders a `GRAPH_SIZE × GRAPH_SIZE`
 *  square showing the histogram backdrop + grid + curve line +
 *  control points. Click empty space to add a point; drag points to
 *  move; drag off the graph to delete. The two endpoints (x=0 and
 *  x=255) can move vertically but can't be deleted or reordered. */
function CurveEditor({
  points,
  onChange,
  color,
  histogram,
}: {
  points: CurvePoint[];
  onChange: (next: CurvePoint[]) => void;
  color: string;
  histogram?: number[];
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const sorted = useMemo(() => [...points].sort((a, b) => a.x - b.x), [points]);

  // Build the curve polyline from the LUT so the displayed line
  // matches the actual transform applied to the image.
  const polylinePath = useMemo(() => {
    const lut = buildLUT(sorted);
    let d = `M 0 ${GRAPH_SIZE - lut[0]}`;
    for (let x = 1; x < 256; x++) {
      d += ` L ${x} ${GRAPH_SIZE - lut[x]}`;
    }
    return d;
  }, [sorted]);

  // Histogram backdrop normalized to graph height. Log scale so dark
  // / sparse regions are still visible without crushing the bright
  // ones.
  const histPath = useMemo(() => {
    if (!histogram || histogram.length !== 256) return '';
    const max = Math.log(1 + Math.max(...histogram));
    if (max === 0) return '';
    let d = `M 0 ${GRAPH_SIZE}`;
    for (let x = 0; x < 256; x++) {
      const h = (Math.log(1 + histogram[x]) / max) * GRAPH_SIZE;
      d += ` L ${x} ${GRAPH_SIZE - h}`;
    }
    d += ` L 255 ${GRAPH_SIZE} Z`;
    return d;
  }, [histogram]);

  const svgPointFromEvent = useCallback((e: PointerEvent | React.PointerEvent): CurvePoint => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * GRAPH_SIZE;
    const y = GRAPH_SIZE - ((e.clientY - rect.top) / rect.height) * GRAPH_SIZE;
    return { x, y };
  }, []);

  const handleSvgPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    // Add a point if the click isn't on an existing point.
    const target = e.target as SVGElement;
    if (target.tagName === 'circle') return; // handled by point-specific listener
    e.preventDefault();
    const pt = svgPointFromEvent(e);
    const x = Math.max(1, Math.min(254, Math.round(pt.x)));
    const y = Math.max(0, Math.min(255, Math.round(pt.y)));
    const next = [...sorted, { x, y }].sort((a, b) => a.x - b.x);
    onChange(next);
    // Find the new point's index in the next array and start dragging it.
    const idx = next.findIndex((p) => p.x === x && p.y === y);
    if (idx >= 0) {
      setDraggingIndex(idx);
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    }
  };

  const handlePointPointerDown = (idx: number) => (e: React.PointerEvent<SVGCircleElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDraggingIndex(idx);
    (e.currentTarget.ownerSVGElement as Element | null)?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (draggingIndex === null) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    // Detect drag-off-graph for delete (only for non-endpoint points).
    const isEndpoint = draggingIndex === 0 || draggingIndex === sorted.length - 1;
    const outside =
      px < -DELETE_DRAG_DISTANCE ||
      px > rect.width + DELETE_DRAG_DISTANCE ||
      py < -DELETE_DRAG_DISTANCE ||
      py > rect.height + DELETE_DRAG_DISTANCE;
    if (outside && !isEndpoint) {
      const next = sorted.filter((_, i) => i !== draggingIndex);
      onChange(next);
      setDraggingIndex(null);
      return;
    }

    const pt = svgPointFromEvent(e);
    let x = Math.round(Math.max(0, Math.min(255, pt.x)));
    const y = Math.round(Math.max(0, Math.min(255, pt.y)));

    // Endpoints stay pinned in x but can move in y.
    if (draggingIndex === 0) x = 0;
    else if (draggingIndex === sorted.length - 1) x = 255;
    else {
      // Don't let middle points cross neighbors.
      const prevX = sorted[draggingIndex - 1].x;
      const nextX = sorted[draggingIndex + 1].x;
      x = Math.max(prevX + 1, Math.min(nextX - 1, x));
    }

    const next = sorted.map((p, i) => (i === draggingIndex ? { x, y } : p));
    onChange(next);
  };

  const handlePointerUp = () => {
    setDraggingIndex(null);
  };

  return (
    <div className="bg-muted rounded-md p-2 select-none">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${GRAPH_SIZE} ${GRAPH_SIZE}`}
        className="w-full h-auto cursor-crosshair touch-none"
        style={{ aspectRatio: '1 / 1' }}
        onPointerDown={handleSvgPointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        {histPath && <path d={histPath} fill="rgba(150, 150, 150, 0.25)" />}

        <g stroke="rgba(128, 128, 128, 0.3)" strokeWidth="1">
          {[64, 128, 192].map((g) => (
            <line key={`v${g}`} x1={g} y1="0" x2={g} y2={GRAPH_SIZE} />
          ))}
          {[64, 128, 192].map((g) => (
            <line key={`h${g}`} x1="0" y1={g} x2={GRAPH_SIZE} y2={g} />
          ))}
        </g>

        <line
          x1="0"
          y1={GRAPH_SIZE}
          x2={GRAPH_SIZE}
          y2="0"
          stroke="rgba(128, 128, 128, 0.4)"
          strokeWidth="1"
          strokeDasharray="2,3"
        />

        <path d={polylinePath} fill="none" stroke={color} strokeWidth="2" />

        {sorted.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={GRAPH_SIZE - p.y}
            r={POINT_RADIUS}
            fill={color}
            stroke="white"
            strokeWidth="1.5"
            className="cursor-grab"
            onPointerDown={handlePointPointerDown(i)}
          />
        ))}
      </svg>
    </div>
  );
}
